import type { NextPage, GetStaticProps, InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";

import { FooterData, getFooterData } from "data/footer";
import { ProductData } from "data/product/products";
import { StorefrontData } from "data/storefront";
import { SingleReview } from "data/videoProductPage";

import {
  getAllProductPathsToPreRender,
  getProductData,
  ProductPathObject,
  getProductFeaturedReviewsData,
  getProductDataFromClassSlug,
} from "~/api/productApi";
import { getStorefronts, getStorefrontWithProducts } from "~/api/storefrontApi";
import { LoadingPage } from "~/components/common/LoadingPage";
import { Product } from "~/components/videoProductPage/Product";
import { getProductIdFromSlug, isUUID } from "~/util/stringUtils";

interface StaticProps {
  productData: ProductData;
  reviewsData: SingleReview[];
  footerData: FooterData;
  storefrontData: StorefrontData;
}

const ProductPageSSG: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  productData,
  reviewsData,
  footerData,
  storefrontData,
}) => {
  const router = useRouter();

  if (router.isFallback) {
    return <LoadingPage />;
  }

  return (
    <Product
      productData={productData}
      reviewsData={reviewsData}
      storefrontData={storefrontData}
      footerData={footerData}
    />
  );
};

export async function getStaticPaths() {
  const productUrlSlugs = await getAllProductPathsToPreRender();



  const paths = productUrlSlugs.map((product_path_object: ProductPathObject) => {
    return {
      params: {
        productIdSlug: product_path_object.productIdSlug,
        storefrontSlug: product_path_object.storefrontSlug,
      },
    };
  });

  return {
    paths: paths,
    fallback: true,
  };
}

export const getStaticProps: ({
  params,
}: {
  params: any;
}) => Promise<{ redirect: { destination: string } } | { props: StaticProps }> = async ({
  params,
}) => {
    // go fetch API, get data, pass down as props

    const slug = params.productIdSlug;
    const storefrontSlug = params.storefrontSlug;


    const productData = await getProductData(slug, storefrontSlug);



    const storefrontData = await getStorefrontWithProducts(storefrontSlug);

    // If we weren't able to find the product, but the storefront exists, just redirect to the storefront.
    if (!productData && storefrontData) {
      return {
        redirect: {
          destination: "/classes/" + storefrontData.slug,
        },
      };
    }

    // If the storefront data also doesn't exist, then just redirect to the homepage.
    if (!storefrontData) {
      return {
        redirect: {
          destination: "/",
        },
      };
    }

    const reviewsData = await getProductFeaturedReviewsData(productData.id);

    return {
      props: {
        productData: productData,
        reviewsData: reviewsData,
        footerData: getFooterData(),
        storefrontData: storefrontData,
      },
    };
  };

export default ProductPageSSG;
